import { useState, useEffect, useRef, useCallback } from "react";
import { Filter, Search, X } from "lucide-react";
import { leadsApi } from "../api/leads.js";

// Google-Sheets-style column header filter: a funnel icon that opens a
// searchable checkbox list of that column's distinct values (fetched from
// the backend, so it reflects the full dataset, not just the current page).
//
// Defaults to leadsApi.getColumnValues; pass `fetchFn` to point this at a
// different scoped endpoint (e.g. Not Ordering's own column-values route)
// without duplicating this UI.
export default function ColumnFilterHeader({ label, field, selected, onApply, extraParams = {}, formatValue, fetchFn }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState(selected);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const isActive = selected.length > 0;

  const fetchValues = useCallback((searchTerm) => {
    setLoading(true);
    const fetcher = fetchFn || leadsApi.getColumnValues;
    fetcher({ field, search: searchTerm || undefined, ...extraParams })
      .then((res) => setValues(res.data ?? []))
      .catch(() => setValues([]))
      .finally(() => setLoading(false));
  }, [field, extraParams, fetchFn]);

  useEffect(() => {
    if (!open) return;
    setDraft(selected);
    setSearch("");
    fetchValues("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchValues(search), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const toggleValue = (val) => {
    setDraft((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));
  };

  const displayLabel = (val) => {
    if (formatValue) return formatValue(val);
    return val === null || val === undefined ? "(empty)" : String(val);
  };

  return (
    <span ref={containerRef} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
      {label}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          border: "none", background: "none", cursor: "pointer", padding: 2,
          display: "inline-flex", color: isActive ? "var(--primary, #16a34a)" : "#9ca3af",
        }}
        title={`Filter ${label}`}
      >
        <Filter size={12} fill={isActive ? "currentColor" : "none"} />
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "100%", left: 0, zIndex: 300, marginTop: 4,
            width: 230, background: "var(--card, #fff)", border: "1px solid var(--border, #e2e8f0)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 10,
            fontWeight: 400, textAlign: "left",
          }}
        >
          <div style={{ position: "relative", marginBottom: 8 }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: 8, color: "#9ca3af" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search values..."
              style={{
                width: "100%", padding: "6px 8px 6px 26px", fontSize: "0.8rem",
                border: "1px solid var(--border, #e2e8f0)", borderRadius: 6, boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <button type="button" onClick={() => setDraft(values)} style={{ background: "none", border: "none", color: "var(--primary, #16a34a)", fontSize: "0.75rem", cursor: "pointer", padding: 0 }}>
              Select all
            </button>
            <button type="button" onClick={() => setDraft([])} style={{ background: "none", border: "none", color: "#6b7280", fontSize: "0.75rem", cursor: "pointer", padding: 0 }}>
              Clear
            </button>
          </div>

          <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid var(--border, #f1f5f9)", borderRadius: 6, padding: 4 }}>
            {loading ? (
              <div style={{ fontSize: "0.78rem", color: "#9ca3af", padding: 6 }}>Loading…</div>
            ) : values.length === 0 ? (
              <div style={{ fontSize: "0.78rem", color: "#9ca3af", padding: 6 }}>No values found</div>
            ) : (
              values.map((val, i) => (
                <label key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 4px", fontSize: "0.8rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={draft.includes(val)}
                    onChange={() => toggleValue(val)}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayLabel(val)}
                  </span>
                </label>
              ))
            )}
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button
              type="button"
              onClick={() => { onApply(draft); setOpen(false); }}
              style={{ flex: 1, padding: "6px 0", fontSize: "0.78rem", fontWeight: 600, borderRadius: 6, border: "none", background: "var(--primary, #16a34a)", color: "#fff", cursor: "pointer" }}
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ padding: "6px 10px", fontSize: "0.78rem", borderRadius: 6, border: "1px solid var(--border, #e2e8f0)", background: "#fff", cursor: "pointer" }}
            >
              Cancel
            </button>
            {isActive && (
              <button
                type="button"
                onClick={() => { onApply([]); setOpen(false); }}
                title="Remove filter"
                style={{ padding: "6px 8px", fontSize: "0.78rem", borderRadius: 6, border: "1px solid var(--border, #e2e8f0)", background: "#fff", cursor: "pointer", color: "#dc2626" }}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
